import React from 'react';
import { Link } from 'react-router-dom';
import './module-placeholder.css';

export type ModuleScene =
  | 'stationary'
  | 'playlist'
  | 'itinerary'
  | 'joystick'
  | 'history'
  | 'coins'
  | 'devices'
  | 'backup'
  | 'account'
  | 'help'
  | 'analytics'
  | 'notifications';

interface ModulePlaceholderProps {
  kicker: string;
  description: string;
  scene: ModuleScene;
}

const SCENE_TONE: Record<ModuleScene, { sky: string; hill: string; accent: string }> = {
  stationary: { sky: 'var(--ml-primary-wash, #efe9ff)', hill: 'var(--ml-primary-soft, #c9b6ff)', accent: 'var(--ml-primary, #7c5cff)' },
  playlist: { sky: 'var(--ml-primary-wash, #f3e9ff)', hill: '#ddc2ff', accent: 'var(--ml-primary, #9b5cff)' },
  itinerary: { sky: '#e7f2ff', hill: '#b9d6ff', accent: 'var(--ml-info, #3b82f6)' },
  joystick: { sky: '#fff1e8', hill: '#ffd0b5', accent: '#f97316' },
  history: { sky: '#e8fbf3', hill: '#b7ead4', accent: 'var(--ml-success, #10b981)' },
  coins: { sky: '#fff6dc', hill: 'var(--ml-accent-soft, #ffe08a)', accent: 'var(--ml-warning, #e0a100)' },
  devices: { sky: '#e7fbf1', hill: '#b6ecd2', accent: 'var(--ml-success, #22c55e)' },
  backup: { sky: '#e8f1ff', hill: '#c5d8ff', accent: '#4f46e5' },
  account: { sky: '#fde8f3', hill: '#f7c2df', accent: '#db2777' },
  help: { sky: '#e8f7ff', hill: '#bfe7fb', accent: '#0284c7' },
  analytics: { sky: 'var(--ml-primary-wash, #efeaff)', hill: 'var(--ml-primary-soft, #d2c4ff)', accent: 'var(--ml-primary, #6d4dff)' },
  notifications: { sky: '#fdecef', hill: '#f8c2cc', accent: 'var(--ml-danger, #e11d48)' },
};

function Motif({ scene, accent }: { scene: ModuleScene; accent: string }) {
  switch (scene) {
    case 'stationary':
      return (
        <g>
          <path d="M160 78c-16 22-28 36-28 52a28 28 0 0 0 56 0c0-16-12-30-28-52z" fill={accent} />
          <circle cx="160" cy="128" r="8" fill="#fff" />
        </g>
      );
    case 'playlist':
      return (
        <g>
          <rect x="118" y="92" width="84" height="18" rx="8" fill={accent} opacity="0.45" />
          <rect x="110" y="114" width="100" height="18" rx="8" fill={accent} opacity="0.7" />
          <rect x="102" y="136" width="116" height="22" rx="9" fill={accent} />
        </g>
      );
    case 'itinerary':
      return (
        <g>
          <path d="M96 150c28-36 52-28 78-52 20-18 40-16 62 6" fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round" />
          <circle cx="96" cy="150" r="8" fill="var(--ml-info, #60a5fa)" />
          <circle cx="160" cy="112" r="8" fill="var(--ml-primary-soft, #a78bfa)" />
          <circle cx="236" cy="104" r="8" fill="var(--ml-success, #34d399)" />
        </g>
      );
    case 'joystick':
      return (
        <g>
          <rect x="108" y="108" width="104" height="58" rx="22" fill={accent} />
          <circle cx="136" cy="136" r="10" fill="#fff" />
          <circle cx="184" cy="128" r="6" fill="#fff" />
          <circle cx="198" cy="144" r="6" fill="var(--ml-accent-soft, #ffe08a)" />
          <path d="M148 108c6-22 22-22 28 0" fill="none" stroke={accent} strokeWidth="6" strokeLinecap="round" />
        </g>
      );
    case 'history':
      return (
        <g>
          <circle cx="160" cy="128" r="34" fill="#fff" />
          <circle cx="160" cy="128" r="28" fill="none" stroke={accent} strokeWidth="6" />
          <path d="M160 112v18l12 8" fill="none" stroke={accent} strokeWidth="5" strokeLinecap="round" />
        </g>
      );
    case 'coins':
      return (
        <g>
          <ellipse cx="146" cy="146" rx="34" ry="12" fill="#f5c542" />
          <ellipse cx="146" cy="136" rx="34" ry="12" fill="var(--ml-accent-soft, #ffe08a)" />
          <ellipse cx="176" cy="128" rx="34" ry="12" fill="#f5c542" />
          <ellipse cx="176" cy="118" rx="34" ry="12" fill="#ffe9a8" />
        </g>
      );
    case 'devices':
      return (
        <g>
          <rect x="118" y="96" width="70" height="48" rx="8" fill="#fff" stroke={accent} strokeWidth="4" />
          <rect x="176" y="112" width="28" height="48" rx="6" fill={accent} />
        </g>
      );
    case 'backup':
      return (
        <g>
          <path d="M112 146h96a28 28 0 0 0-8-54 34 34 0 0 0-64-6 26 26 0 0 0-24 60z" fill="#fff" stroke={accent} strokeWidth="4" />
          <path d="M148 132v22M148 154l-8-8M148 154l8-8" fill="none" stroke={accent} strokeWidth="4" strokeLinecap="round" />
        </g>
      );
    case 'account':
      return (
        <g>
          <circle cx="160" cy="112" r="18" fill={accent} />
          <path d="M122 164c6-22 22-32 38-32s32 10 38 32" fill={accent} />
        </g>
      );
    case 'help':
      return (
        <g>
          <circle cx="160" cy="128" r="32" fill="none" stroke={accent} strokeWidth="8" />
          <circle cx="160" cy="128" r="8" fill={accent} />
          <path d="M160 96v-8M160 168v8M128 128h-8M200 128h8" stroke={accent} strokeWidth="6" strokeLinecap="round" />
        </g>
      );
    case 'analytics':
      return (
        <g>
          <rect x="112" y="132" width="18" height="36" rx="6" fill={accent} opacity="0.45" />
          <rect x="140" y="112" width="18" height="56" rx="6" fill={accent} opacity="0.7" />
          <rect x="168" y="96" width="18" height="72" rx="6" fill={accent} />
        </g>
      );
    case 'notifications':
      return (
        <g>
          <path d="M160 96a28 28 0 0 0-28 28v16l-8 12h72l-8-12v-16a28 28 0 0 0-28-28z" fill={accent} />
          <circle cx="180" cy="104" r="8" fill="#fff" stroke={accent} strokeWidth="3" />
          <path d="M150 156a10 10 0 0 0 20 0" fill="none" stroke={accent} strokeWidth="4" />
        </g>
      );
    default:
      return null;
  }
}

function SceneArt({ scene }: { scene: ModuleScene }) {
  const tone = SCENE_TONE[scene];
  return (
    <svg className="module-empty__svg" viewBox="0 0 320 230" aria-hidden="true">
      <rect width="320" height="230" rx="28" fill={tone.sky} />
      <circle cx="250" cy="48" r="22" fill="var(--ml-accent-soft, #ffe08a)" />
      <circle cx="58" cy="62" r="16" fill="#fff" opacity="0.8" />
      <path d="M0 150c48-28 78 10 130-8 42-14 70 16 110-4 28-14 52 4 80-8v100H0z" fill={tone.hill} opacity="0.85" />
      <path d="M0 182c60-24 90 16 150-2 48-14 70 18 170-10v60H0z" fill="#fff" opacity="0.55" />
      <Motif scene={scene} accent={tone.accent} />
    </svg>
  );
}

const ModulePlaceholder: React.FC<ModulePlaceholderProps> = ({
  kicker,
  description,
  scene,
}) => (
  <section className="module-empty">
    <div className="module-empty__art">
      <SceneArt scene={scene} />
    </div>
    <div className="module-empty__copy">
      <p className="module-empty__kicker">{kicker}</p>
      <p className="module-empty__text">{description}</p>
      <p className="module-empty__note">In development</p>
      <Link to="/app" className="module-empty__back">
        Back to dashboard
      </Link>
    </div>
  </section>
);

export const LocationHistoryPage: React.FC = () => (
  <ModulePlaceholder
    scene="history"
    kicker="Activity"
    description="A timeline of mocked places will show up here. Filters and map replay are still in development."
  />
);

export const HelpPage: React.FC = () => (
  <ModulePlaceholder
    scene="help"
    kicker="Support"
    description="Setup guides and a way to reach support will live here. The help desk is still in development. Articles already cover the current guides."
  />
);

export default ModulePlaceholder;
